import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import SearchableSelect from "../ui/SearchableSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";
import { Plus, Edit, Trash2, Wrench, Sparkles, Clock, FileText, Tag, FolderOpen, ChevronDown, ChevronUp, X } from "lucide-react";
import { Switch } from "../ui/switch";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DURATION_COLOR = (h) => {
  if (h <= 1) return "bg-emerald-500/15 text-emerald-500 border-emerald-500/20";
  if (h <= 2) return "bg-blue-500/15 text-blue-500 border-blue-500/20";
  if (h <= 4) return "bg-amber-500/15 text-amber-500 border-amber-500/20";
  return "bg-red-500/15 text-red-500 border-red-500/20";
};

const ACCENT_COLOR = (h) => {
  if (h <= 1) return "border-l-emerald-500";
  if (h <= 2) return "border-l-blue-500";
  if (h <= 4) return "border-l-amber-500";
  return "border-l-red-500";
};

const JobTypesManager = ({
  jobTypes,
  suggestions,
  businessType,
  jobTypeDialogOpen,
  setJobTypeDialogOpen,
  editingJobType,
  jobTypeForm,
  setJobTypeForm,
  resetJobTypeForm,
  editJobType,
  saveJobType,
  importSuggestions,
  confirmDelete,
}) => {
  const token = localStorage.getItem("token");
  const authConfig = { headers: { Authorization: `Bearer ${token}` } };

  // Categories state
  const [categories, setCategories] = useState([]);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catName, setCatName] = useState("");
  const [catSubCategories, setCatSubCategories] = useState([]);
  const [newSubName, setNewSubName] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [expandedCat, setExpandedCat] = useState(null);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/service-categories`, authConfig);
      setCategories(res.data || []);
    } catch { /* ignore */ }
  };

  const saveCat = async () => {
    if (!catName.trim()) { toast.error("Category name is required"); return; }
    try {
      const payload = { name: catName.trim(), sub_categories: catSubCategories };
      if (editingCat) {
        await axios.put(`${API}/api/settings/service-categories/${editingCat.category_id}`, payload, authConfig);
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/api/settings/service-categories`, payload, authConfig);
        toast.success("Category created");
      }
      setCatDialogOpen(false);
      setCatName("");
      setCatSubCategories([]);
      setEditingCat(null);
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save category");
    }
  };

  const addSubCategory = () => {
    if (!newSubName.trim()) return;
    const subId = `sub_${Date.now().toString(36)}`;
    setCatSubCategories(prev => [...prev, { sub_id: subId, name: newSubName.trim() }]);
    setNewSubName("");
  };

  const removeSubCategory = (subId) => {
    setCatSubCategories(prev => prev.filter(s => s.sub_id !== subId));
  };

  const openEditCat = (cat) => {
    setEditingCat(cat);
    setCatName(cat.name);
    setCatSubCategories(cat.sub_categories || []);
    setCatDialogOpen(true);
  };

  const deleteCat = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Services in this category will become uncategorized.`)) return;
    try {
      await axios.delete(`${API}/api/settings/service-categories/${cat.category_id}`, authConfig);
      toast.success("Category deleted");
      fetchCategories();
    } catch {
      toast.error("Failed to delete category");
    }
  };

  // Group services by category
  const grouped = useMemo(() => {
    const filtered = filterCat === "all" ? jobTypes : jobTypes.filter(jt => (jt.category || "") === (filterCat === "uncategorized" ? "" : filterCat));
    if (filterCat !== "all") return { [filterCat === "uncategorized" ? "" : filterCat]: filtered };

    const groups = {};
    for (const jt of jobTypes) {
      const cat = jt.category || "";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(jt);
    }
    // Sort: named categories first (by order), uncategorized last
    const sorted = {};
    const catOrder = categories.map(c => c.name);
    for (const name of catOrder) {
      if (groups[name]) sorted[name] = groups[name];
    }
    // Any categories in services but not in the categories list
    for (const [name, services] of Object.entries(groups)) {
      if (name && !sorted[name]) sorted[name] = services;
    }
    // Uncategorized last
    if (groups[""]) sorted[""] = groups[""];
    return sorted;
  }, [jobTypes, categories, filterCat]);

  return (
    <div className="space-y-6">
      {/* Categories Card */}
      <Card className="glass-card border-theme" data-testid="service-categories-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
          <div>
            <CardTitle className="text-foreground font-['Manrope'] text-lg">Service Categories</CardTitle>
            <CardDescription className="text-muted-foreground text-sm">
              Organize your services into categories
            </CardDescription>
          </div>
          <Dialog open={catDialogOpen} onOpenChange={(open) => { setCatDialogOpen(open); if (!open) { setCatName(""); setCatSubCategories([]); setEditingCat(null); setNewSubName(""); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#0046BE] hover:bg-[#003DA5] text-white text-xs sm:text-sm rounded-full px-4" data-testid="add-category-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-background border-border max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-foreground">{editingCat ? "Edit" : "Add"} Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Category Name *</Label>
                  <Input
                    value={catName}
                    onChange={(e) => setCatName(e.target.value)}
                    className="input-glass"
                    placeholder="e.g., Automotive, Residential, Commercial"
                    data-testid="category-name-input"
                  />
                </div>
                {/* Sub-categories */}
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Sub-Categories</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newSubName}
                      onChange={(e) => setNewSubName(e.target.value)}
                      className="input-glass flex-1"
                      placeholder="e.g., Key Programming, Lockout"
                      data-testid="sub-category-input"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubCategory(); } }}
                    />
                    <Button size="sm" variant="outline" onClick={addSubCategory} className="border-theme shrink-0" data-testid="add-sub-btn">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {catSubCategories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {catSubCategories.map(sub => (
                        <span key={sub.sub_id} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-muted/50 border border-border text-xs text-foreground" data-testid={`sub-item-${sub.sub_id}`}>
                          {sub.name}
                          <button onClick={() => removeSubCategory(sub.sub_id)} className="text-muted-foreground hover:text-red-400 ml-0.5">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="ghost" onClick={() => setCatDialogOpen(false)} className="text-muted-foreground">Cancel</Button>
                  <Button onClick={saveCat} className="btn-primary" data-testid="save-category-btn">
                    {editingCat ? "Update" : "Create"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="pt-0">
          {categories.length > 0 ? (
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat.category_id} className="rounded-lg border border-theme bg-theme-surface" data-testid={`category-item-${cat.category_id}`}>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      onClick={() => setExpandedCat(expandedCat === cat.category_id ? null : cat.category_id)}
                    >
                      <Tag className="w-3.5 h-3.5 text-[#0046BE] shrink-0" />
                      <span className="text-foreground text-sm font-medium truncate">{cat.name}</span>
                      {(cat.sub_categories?.length > 0) && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-theme text-muted-foreground shrink-0">{cat.sub_categories.length} sub</Badge>
                      )}
                      {cat.sub_categories?.length > 0 && (
                        expandedCat === cat.category_id
                          ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      )}
                    </button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground shrink-0" onClick={() => openEditCat(cat)}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400/70 hover:text-red-400 shrink-0" onClick={() => deleteCat(cat)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  {expandedCat === cat.category_id && cat.sub_categories?.length > 0 && (
                    <div className="px-3 pb-2 flex flex-wrap gap-1.5 border-t border-theme pt-2 mx-2 mb-1">
                      {cat.sub_categories.map(sub => (
                        <span key={sub.sub_id} className="text-xs px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground border border-border">
                          {sub.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No categories yet. Create categories to organize your services.</p>
          )}
        </CardContent>
      </Card>

      {/* Services Card */}
      <Card className="glass-card border-theme" data-testid="job-types-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
          <div>
            <CardTitle className="text-foreground font-['Manrope'] text-lg">Services / Job Types</CardTitle>
            <CardDescription className="text-muted-foreground text-sm">
              Define the services you offer with estimated durations
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Category Filter — searchable for orgs with many categories */}
            {categories.length > 0 && (
              <SearchableSelect
                value={filterCat}
                onChange={setFilterCat}
                options={[
                  { value: "all", label: "All Categories" },
                  ...categories.map(c => ({ value: c.name, label: c.name })),
                  { value: "uncategorized", label: "Uncategorized" },
                ]}
                placeholder="All Categories"
                searchThreshold={4}
                className="w-[150px]"
                triggerClassName="h-8 text-xs border-theme bg-theme-surface text-foreground"
                triggerIcon={<FolderOpen className="w-3.5 h-3.5 text-muted-foreground" />}
                data-testid="filter-category-select"
              />
            )}
            {suggestions.length > 0 && (
              <Button onClick={importSuggestions} variant="outline" size="sm" className="border-theme text-muted-foreground hover:text-foreground text-xs sm:text-sm" data-testid="import-suggestions-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-[#FFCC00]" /> <span className="hidden sm:inline">Suggested</span> ({suggestions.length})
              </Button>
            )}
            <Dialog open={jobTypeDialogOpen} onOpenChange={(open) => { setJobTypeDialogOpen(open); if (!open) resetJobTypeForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#0046BE] hover:bg-[#003DA5] text-white text-xs sm:text-sm rounded-full px-4" data-testid="add-service-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Service
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-background border-border max-w-md max-h-[85vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle className="text-foreground">{editingJobType ? "Edit" : "Add"} Service</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Service Name *</Label>
                    <Input
                      value={jobTypeForm.name}
                      onChange={(e) => setJobTypeForm({ ...jobTypeForm, name: e.target.value })}
                      className="input-glass"
                      placeholder="e.g., Lock Replacement"
                      data-testid="service-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Category</Label>
                    <Select value={jobTypeForm.category || "__none__"} onValueChange={(v) => setJobTypeForm({ ...jobTypeForm, category: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="input-glass" data-testid="service-category-select">
                        <SelectValue placeholder="Select category (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No Category</SelectItem>
                        {categories.map(c => (
                          <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Description</Label>
                    <Textarea
                      value={jobTypeForm.description}
                      onChange={(e) => setJobTypeForm({ ...jobTypeForm, description: e.target.value })}
                      className="input-glass min-h-[52px] text-sm"
                      placeholder="Brief description of this service..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Default Duration (hours)</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.5"
                        min="0.25"
                        value={jobTypeForm.default_duration}
                        onChange={(e) => setJobTypeForm({ ...jobTypeForm, default_duration: e.target.value })}
                        className="input-glass pl-9"
                        placeholder="1"
                        data-testid="service-duration-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-theme bg-theme-surface p-3">
                    <div className="flex items-center gap-2.5">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label className="text-sm text-foreground font-medium">Hide parts in tech PDF</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Parts won't appear in the technician report for this service</p>
                      </div>
                    </div>
                    <Switch
                      checked={!!jobTypeForm.hide_parts_in_pdf}
                      onCheckedChange={(checked) => setJobTypeForm({ ...jobTypeForm, hide_parts_in_pdf: checked })}
                      data-testid="hide-parts-pdf-toggle"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-2 sticky bottom-0 bg-background pb-1">
                    <Button variant="ghost" onClick={() => setJobTypeDialogOpen(false)} className="text-muted-foreground">Cancel</Button>
                    <Button onClick={saveJobType} className="btn-primary" data-testid="save-service-btn">
                      {editingJobType ? "Update" : "Create"} Service
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {jobTypes.length > 0 ? (
            <div className="space-y-6">
              {Object.entries(grouped).map(([catName, services]) => (
                <div key={catName || "__uncategorized__"}>
                  {/* Category header (only show if there are categories defined) */}
                  {categories.length > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                      <h3 className="text-sm font-semibold text-foreground">{catName || "Uncategorized"}</h3>
                      <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-theme text-muted-foreground">{services.length}</Badge>
                    </div>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {services.map((jt, i) => {
                      const dur = parseFloat(jt.default_duration) || 1;
                      const durColor = DURATION_COLOR(dur);
                      const accent = ACCENT_COLOR(dur);
                      return (
                        <div
                          key={jt.service_id}
                          className={`relative rounded-xl bg-theme-surface border border-theme border-l-[3px] ${accent} overflow-hidden transition-all hover:border-white/20`}
                          data-testid={`service-item-${i}`}
                        >
                          <div className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-[#0046BE]/15 flex items-center justify-center shrink-0">
                                  <Wrench className="w-4.5 h-4.5 text-[#0046BE]" />
                                </div>
                                <div className="min-w-0">
                                  <h4 className="font-semibold text-foreground text-sm truncate">{jt.name}</h4>
                                  {jt.description && (
                                    <p className="text-muted-foreground text-xs truncate mt-0.5">{jt.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-0.5 shrink-0">
                                <Button variant="ghost" size="icon" onClick={() => editJobType(jt)} className="h-7 w-7 text-muted-foreground hover:text-foreground">
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => confirmDelete("jobType", jt.service_id, jt.name)} className="h-7 w-7 text-red-400/70 hover:text-red-400">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-[11px] border ${durColor}`}>
                                <Clock className="w-3 h-3 mr-1" />
                                {dur}h duration
                              </Badge>
                              {jt.category && (
                                <Badge variant="outline" className="text-[11px] border-theme text-muted-foreground">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {jt.category}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="w-14 h-14 rounded-2xl bg-[#0046BE]/10 flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-7 h-7 text-[#0046BE]/40" />
              </div>
              <p className="text-foreground font-medium">No services configured</p>
              <p className="text-muted-foreground/60 text-sm mt-1 max-w-[260px] mx-auto">
                {suggestions.length > 0
                  ? `Click "Suggested Services" to add recommended services for ${businessType}`
                  : "Add services to define the types of work you offer"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JobTypesManager;
